<template>
  <div class="mb-10">
    <div class="d-flex align-center">
      <h3>アルバム一覧</h3>
      <v-spacer />
      <v-btn to="/memoru/upload" class="mr-2" color="primary" variant="outlined">
        アップロード
      </v-btn>
      <v-btn @click="createAlbumDialog = true" color="primary" variant="flat">
        <v-icon start>mdi-plus</v-icon>
        アルバム作成
      </v-btn>
    </div>

    <v-card variant="flat" color="grey-lighten-5" class="mt-2">
      <v-container>
        <v-row>
          <v-col cols="3">
            <h5>年度</h5>
            <v-text-field v-model="searchFilters.year" variant="outlined" hide-details density="compact"
              :disabled="loading" color="primary"></v-text-field>
          </v-col>
          <v-col cols="3">
            <h5>公開設定</h5>
            <v-select v-model="searchFilters.isPublished" :items="isPublishedItems" variant="outlined" hide-details
              density="compact" :disabled="loading" color="primary"></v-select>
          </v-col>
          <v-col cols="3">
            <h5>売上枚数(下限)</h5>
            <v-text-field v-model="searchFilters.purchaseCountMin" variant="outlined" hide-details density="compact"
              suffix="枚" :disabled="loading" color="primary"></v-text-field>
          </v-col>
          <v-col cols="3">
            <h5>クラス</h5>
            <v-select v-model="searchFilters.nurseryClassIds" :items="classes"
              :item-title="(item) => item.year + '年度 ' + item.className" item-value="classId" multiple
              variant="outlined" hide-details density="compact" :disabled="loading" color="primary"></v-select>
          </v-col>
        </v-row>
        <v-row>
          <v-spacer />
          <v-col cols="4">
            <v-btn @click="listAlbums" :loading="loading" color="primary" variant="flat" block>検索</v-btn>
          </v-col>
          <v-spacer />
        </v-row>
      </v-container>
    </v-card>

    <div v-if="albums" class="pt-4 d-flex align-center">
      <h5>計{{ albums.total || 0 }}件</h5>
      <v-spacer />
    </div>

    <div class="pt-4">
      <v-row v-if="albums">
        <v-col cols="3" v-for="(item, i) in albums.items" :key="i">
          <memoru-album-card :album="item"></memoru-album-card>
        </v-col>
      </v-row>
      <v-pagination class="mt-6" v-if="albums" v-model="page" @update:model-value="pageClick"
        :length="Math.ceil(albums.total / searchFilters.limit)" active-color="primary">
      </v-pagination>
    </div>

    <v-form v-model="createAlbumForm">
      <v-dialog v-model="createAlbumDialog" max-width="600px" persistent>
        <v-card>
          <v-card-title> アルバム作成 </v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12">
                  <v-text-field v-model="createAlbumInput.title" :rules="[required]" label="タイトル*" hide-details
                    variant="outlined" density="compact" color="primary"></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field v-model="createAlbumInput.year" :rules="[required]" label="年度*" hide-details suffix="年度"
                    variant="outlined" density="compact" color="primary"></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-select v-model="createAlbumInput.nurseryClassIds" :items="classes"
                    :item-title="(item) => item.year + '年度 ' + item.className" item-value="classId" label="クラス"
                    hide-details variant="outlined" density="compact" multiple chips color="primary"></v-select>
                </v-col>
                <v-col cols="12">
                  <v-radio-group v-model="albumType" inline color="primary">
                    <v-radio label="日常" value="ordinary"></v-radio>
                    <v-radio label="行事" value="event"></v-radio>
                    <v-radio label="その他" value="undefined"></v-radio>
                  </v-radio-group>
                </v-col>
                <v-col cols="12" v-if="albumType === 'ordinary' || albumType === 'event'">
                  <v-select v-model="selectedMonth" :items="monthItems" item-title="title" item-value="value"
                    label="月を選択" hide-details variant="outlined" density="compact" color="primary"></v-select>
                </v-col>
                <v-col cols="6">
                  <v-text-field v-if="showAvailable" type="date" v-model="internalDateAfter" label="公開開始日*" hide-details
                    clearable variant="outlined" density="compact"
                    :disabled="albumType === 'ordinary' || albumType === 'event'"
                    @update:model-value="createCombinedDateTimeAfter" color="primary"></v-text-field>
                </v-col>
                <v-col cols="6">
                  <v-text-field v-if="showAvailable" type="time" v-model="internalTimeAfter" label="公開開始時*" hide-details
                    clearable variant="outlined" density="compact"
                    :disabled="albumType === 'ordinary' || albumType === 'event'"
                    @update:model-value="createCombinedDateTimeAfter" color="primary"></v-text-field>
                </v-col>
                <v-col cols="6">
                  <v-text-field v-if="showAvailable" type="date" v-model="internalDateBefore" label="公開終了日" hide-details
                    clearable variant="outlined" density="compact"
                    :disabled="albumType === 'ordinary' || albumType === 'event'"
                    @update:model-value="createCombinedDateTimeBefore" color="primary"></v-text-field>
                </v-col>
                <v-col cols="6">
                  <v-text-field v-if="showAvailable" type="time" v-model="internalTimeBefore" label="公開終了時" hide-details
                    clearable variant="outlined" density="compact"
                    :disabled="albumType === 'ordinary' || albumType === 'event'"
                    @update:model-value="createCombinedDateTimeBefore" color="primary"></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-textarea v-model="createAlbumInput.comment" label="コメント" hide-details variant="outlined"
                    density="compact" color="primary"></v-textarea>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-container>
              <v-row>
                <v-btn @click="createAlbumDialog = false" color="grey" variant="text">
                  キャンセル
                </v-btn>
                <v-spacer />
                <v-btn @click="createAlbum" :disabled="!createAlbumForm" :loading="loading" color="primary"
                  variant="flat">
                  作成
                </v-btn>
              </v-row>
            </v-container>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-form>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import MemoruAlbumCard from "@/components/atoms/MemoruAlbumCard.vue";
import { memoruModule } from "@/store/dataModules/memoruModule";
import LocalDataService from "@/service/LocalDataService";
import {
  MultimediaAlbumListResponse,
  NurseryClassResponse,
} from "chaild-api/lib";
import ApiMemoru from "@/api/ApiMemoru";
import { snackbarModule } from "@/store/viewModules/snackbarModule";
import ValidationMixin from "@/mixins/ValidationMixin";
import dayjs from "dayjs";
import { classModule } from "@/store/dataModules/classModule";
import { isPublishedItems } from "@/constants/memoru";

export default defineComponent({
  name: "MemoruAlbum",

  mixins: [ValidationMixin],

  components: {
    MemoruAlbumCard,
  },

  beforeRouteLeave() {
    this.saveSearchFilters();
  },

  async mounted() {
    const saved = sessionStorage.getItem('memoruAlbumSearchFilters');
    if (saved) {
      this.searchFilters = JSON.parse(saved);
    } else {
      const memoruAlbumPage = sessionStorage.getItem('memoruAlbumPage');
      if (memoruAlbumPage) this.page = Number(memoruAlbumPage);
    }
    await this.listAlbums();
    await this.listClasses();
  },

  computed: {
    albums: function (): MultimediaAlbumListResponse | null {
      return memoruModule.albums;
    },
    classes(): NurseryClassResponse[] {
      if (this.createAlbumInput.year) {
        return classModule.classes.filter((c) => {
          return String(c.year) == this.createAlbumInput.year;
        });
      } else {
        return classModule.classes;
      }
    },
    page: {
      get(): number {
        if (this.searchFilters.skip == 0) {
          return 1;
        }
        return Math.ceil(
          this.searchFilters.skip / (this.searchFilters.limit - 1)
        );
      },
      set(value: number) {
        this.searchFilters.skip = (value - 1) * this.searchFilters.limit;
        this.listAlbums();
      },
    },
  },

  data() {
    return {
      loading: false,
      searchFilters: {
        year: "",
        nurseryClassIds: [],
        purchaseCountMin: "",
        isPublished: undefined as boolean | undefined,
        limit: 20,
        skip: 0,
      },
      isPublishedItems: isPublishedItems,
      createAlbumDialog: false,
      createAlbumForm: false,
      createAlbumInput: {
        title: "",
        year: "",
        nurseryClassIds: [],
        comment: "",
        availableAfter: "",
        availableBefore: "" as string | null,
      },
      showAvailable: true,
      internalDateAfter: '' as string | null,
      internalTimeAfter: '00:00' as string | null,
      internalDateBefore: '' as string | null,
      internalTimeBefore: '' as string | null,
      albumType: 'ordinary' as 'ordinary' | 'event' | undefined,
      selectedMonth: undefined as number | undefined,
      monthItems: [
        { title: '4月', value: 4 },
        { title: '5月', value: 5 },
        { title: '6月', value: 6 },
        { title: '7月', value: 7 },
        { title: '8月', value: 8 },
        { title: '9月', value: 9 },
        { title: '10月', value: 10 },
        { title: '11月', value: 11 },
        { title: '12月', value: 12 },
        { title: '1月', value: 1 },
        { title: '2月', value: 2 },
        { title: '3月', value: 3 },
      ],
      monthlySchedules: {
        ordinary: {
          1: { afterDate: '01-01', afterTime: '00:00', beforeDate: '01-28', beforeTime: '23:59' },
          2: { afterDate: '02-01', afterTime: '00:00', beforeDate: '02-25', beforeTime: '23:59' },
          3: { afterDate: '03-01', afterTime: '00:00', beforeDate: '03-25', beforeTime: '23:59' },
          4: { afterDate: '04-01', afterTime: '00:00', beforeDate: '04-23', beforeTime: '23:59' },
          5: { afterDate: '05-01', afterTime: '00:00', beforeDate: '05-28', beforeTime: '23:59' },
          6: { afterDate: '06-01', afterTime: '00:00', beforeDate: '06-25', beforeTime: '23:59' },
          7: { afterDate: '07-01', afterTime: '00:00', beforeDate: '07-23', beforeTime: '23:59' },
          8: { afterDate: '08-01', afterTime: '00:00', beforeDate: '08-27', beforeTime: '23:59' },
          9: { afterDate: '09-01', afterTime: '00:00', beforeDate: '09-24', beforeTime: '23:59' },
          10: { afterDate: '10-01', afterTime: '00:00', beforeDate: '10-29', beforeTime: '23:59' },
          11: { afterDate: '11-02', afterTime: '00:00', beforeDate: '11-26', beforeTime: '23:59' },
          12: { afterDate: '12-01', afterTime: '00:00', beforeDate: '12-24', beforeTime: '23:59' },
        },
        event: {
          1: { afterDate: '01-29', afterTime: '00:00', beforeDate: '02-25', beforeTime: '23:59' },
          2: { afterDate: '02-26', afterTime: '00:00', beforeDate: '03-25', beforeTime: '23:59' },
          3: { afterDate: '03-26', afterTime: '00:00', beforeDate: '04-22', beforeTime: '23:59' },
          4: { afterDate: '04-24', afterTime: '00:00', beforeDate: '05-28', beforeTime: '23:59' },
          5: { afterDate: '05-29', afterTime: '00:00', beforeDate: '06-25', beforeTime: '23:59' },
          6: { afterDate: '06-26', afterTime: '00:00', beforeDate: '07-23', beforeTime: '23:59' },
          7: { afterDate: '07-24', afterTime: '00:00', beforeDate: '08-27', beforeTime: '23:59' },
          8: { afterDate: '08-28', afterTime: '00:00', beforeDate: '09-24', beforeTime: '23:59' },
          9: { afterDate: '09-25', afterTime: '00:00', beforeDate: '10-29', beforeTime: '23:59' },
          10: { afterDate: '10-30', afterTime: '00:00', beforeDate: '11-26', beforeTime: '23:59' },
          11: { afterDate: '11-27', afterTime: '00:00', beforeDate: '12-24', beforeTime: '23:59' },
          12: { afterDate: '12-25', afterTime: '00:00', beforeDate: '01-28', beforeTime: '23:59' },
        },
      } as Record<'ordinary' | 'event', Record<number, { afterDate: string; afterTime: string; beforeDate: string; beforeTime: string }>>,
    };
  },

  watch: {
    albumType(newType: 'ordinary' | 'event' | undefined) {
      this.selectedMonth = undefined;
      if (newType === 'ordinary' || newType === 'event') {
        this.internalDateAfter = '';
        this.internalTimeAfter = '00:00';
        this.internalDateBefore = '';
        this.internalTimeBefore = '';
        this.createCombinedDateTimeAfter();
        this.createCombinedDateTimeBefore();
      }
    },
    selectedMonth(newMonth: number | undefined) {
      if (newMonth && (this.albumType === 'ordinary' || this.albumType === 'event')) {
        this.applyMonthlySchedule();
      }
    },
    internalDateBefore: 'createCombinedDateTimeBefore',
    internalTimeBefore: 'createCombinedDateTimeBefore',
    'createAlbumInput.availableBefore': {
      handler(newValue) {
        if (newValue) {
          const parts = newValue.split('T');
          if (parts.length === 2) {
            this.internalDateBefore = parts[0];
            this.internalTimeBefore = parts[1];
          }
        } else {
          this.internalDateBefore = '';
          this.internalTimeBefore = '';
        }
      },
      immediate: true
    },

    internalDateAfter: 'createCombinedDateTimeAfter',
    internalTimeAfter: 'createCombinedDateTimeAfter',
    'createAlbumInput.availableAfter': {
      handler(newValue) {
        if (newValue) {
          const parts = newValue.split('T');
          if (parts.length === 2) {
            this.internalDateAfter = parts[0];
            this.internalTimeAfter = parts[1];
          }
        } else {
          this.internalDateAfter = '';
          this.internalTimeAfter = '';
        }
      },
      immediate: true
    },
  },

  methods: {
    applyMonthlySchedule() {
      if (!this.albumType || !this.selectedMonth) return;
      const schedule = this.monthlySchedules[this.albumType]?.[this.selectedMonth];
      if (!schedule) return;
      let year = this.createAlbumInput.year || String(new Date().getFullYear());
      year = this.selectedMonth <= 3 ? String(Number(year) + 1) : year;
      this.internalDateAfter = `${year}-${schedule.afterDate}`;
      this.internalTimeAfter = schedule.afterTime;
      if (this.selectedMonth === 12 && this.albumType === 'event') {
        this.internalDateBefore = `${String(Number(year) + 1)}-${schedule.beforeDate}`;
      } else {
        this.internalDateBefore = `${year}-${schedule.beforeDate}`;
      }
      this.internalTimeBefore = schedule.beforeTime;
      this.createCombinedDateTimeAfter();
      this.createCombinedDateTimeBefore();
    },
    async pageClick(pageNumber: number) {
      if (pageNumber) sessionStorage.setItem('memoruAlbumPage', String(pageNumber));
    },
    saveSearchFilters() {
      sessionStorage.setItem('memoruAlbumSearchFilters', JSON.stringify(this.searchFilters));
    },
    createCombinedDateTimeAfter() {
      if (this.internalDateAfter) {
        const combinedDateTimeAfter = `${this.internalDateAfter}T${this.internalTimeAfter || '00:00'}`;
        this.createAlbumInput.availableAfter = combinedDateTimeAfter;
      } else {
        this.createAlbumInput.availableAfter = '';
      }
    },
    createCombinedDateTimeBefore() {
      if (this.internalDateBefore) {
        const combinedDateTimeBefore = `${this.internalDateBefore}T${this.internalTimeBefore || '00:00'}`;
        this.createAlbumInput.availableBefore = combinedDateTimeBefore;
      } else {
        this.createAlbumInput.availableBefore = '';
      }
    },
    async listAlbums() {
      this.loading = true;
      this.saveSearchFilters();
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        await memoruModule.listAlbums({
          ...this.searchFilters,
          nurseryId,
        });
      }
      this.loading = false;
    },
    async createAlbum() {
      this.loading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        const response = await ApiMemoru.createAlbum({
          nurseryId,
          ...this.createAlbumInput,
        }).catch(() => (this.loading = false));
        if (response) {
          snackbarModule.snackOn({
            message: "アルバムを作成しました",
          });
          this.createAlbumDialog = false;
          await this.listAlbums();
        }

        this.internalDateAfter = null;
        this.internalTimeAfter = null;
        this.internalDateBefore = null;
        this.internalTimeBefore = null;
        this.albumType = undefined;
        this.selectedMonth = undefined;

        this.showAvailable = false;
        this.$nextTick(() => {
          this.showAvailable = true;
        });
      }
      this.loading = false;
    },
    async listClasses() {
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        await classModule.listClass({
          nurseryId,
        });
      }
    },
  },
});
</script>

<style scoped>
:deep(.v-pagination__item--is-active .v-btn) {
  background-color: rgb(var(--v-theme-primary)) !important;
  color: white !important;
  font-weight: bold;
  transform: scale(1.2);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
}
</style>